// Refund flow of a single order payment, shared by the POS V2 payments box,
// the legacy payments list and the caja movements list: confirm -> admin PIN
// (when the user lacks OrdersCancel) -> PUT status -> toast -> refresh the
// viewed order (when the caller has one).
import { onScopeDispose, ref, Ref } from 'vue';
import PaymentsService from '@/api/services/payments.service';
import { PaymentStatus } from '@/enums/PaymentEnums';
import { PermissionType } from '@/enums/PermissionType';
import { AlertType } from '@/interfaces/IAlert';
import { IAuthData } from '@/interfaces/IAuthData';
import { IOrder } from '@/interfaces/IOrder';
import { IPayment } from '@/interfaces/IPayment';
import { displayPrice } from '@/utils';
import { i18n } from '@/plugins/i18n';
import { useAuth } from '@/composition/use-auth';
import { useModal } from '@/composition/use-modal';
import { useOrders } from '@/composition/use-orders';
import { useUi } from '@/composition/use-ui';
import { useRestaurantStore } from '@/stores/restaurant.store';
import ModalAdminPin from '@/components/modals/ModalAdminPin.vue';

export interface IRefundPaymentParams {
  payment: Pick<IPayment, 'id' | 'total'>;
  /** THIS order's share of the payment (what the confirm copy quotes). */
  amount: number;
  /** The payment also covers other orders: it is refunded in full anyway. */
  shared: boolean;
}

/**
 * Server facts: `PUT /v2/payments/:id/status` hard-requires CajaManage (no
 * PIN override), then checks OrdersCancel against the PIN's authData.
 * Callers hide the action when the user lacks CajaManage.
 *
 * @param order The order on screen, refreshed after a refund. Callers without
 * an order in view (caja movements) omit it and refresh their own data.
 */
export function usePaymentRefund(order?: Ref<IOrder>) {
  const modals = useModal();
  const auth = useAuth();
  const ui = useUi();
  const ordersHelper = useOrders();
  const restaurantStore = useRestaurantStore();

  const isRefunding = ref(false);
  // The host modal may close while the request is in flight; touching its
  // reactive state afterwards is what the renderer-safety rule forbids.
  let disposed = false;
  onScopeDispose(() => {
    disposed = true;
  });

  function t(key: string, params?: Record<string, unknown>): string {
    return String(i18n.t(key, params));
  }

  function formatMoney(value: number): string {
    return `${restaurantStore.currency.symbol}${displayPrice(value)}`;
  }

  /** Resolves true when the payment was refunded. */
  async function refundPayment(params: IRefundPaymentParams): Promise<boolean> {
    if (isRefunding.value) return false;
    const { payment, amount, shared } = params;
    // The dialogs below take arbitrarily long; refresh the order that was
    // on screen when the cashier asked, even if the host swapped it since.
    const viewedOrder = order?.value ?? null;

    // Confirm before the PIN so a cancelled confirm never wastes one.
    const confirmed = await modals.confirm({
      title: t('posV2.paymentsRefundTitle'),
      message: shared
        ? t('posV2.paymentsRefundConfirmShared', {
            total: formatMoney(payment.total),
          })
        : t('posV2.paymentsRefundConfirm', { amount: formatMoney(amount) }),
    });
    if (!confirmed) return false;

    let authData: IAuthData | undefined;
    if (!auth.hasPermission(PermissionType.OrdersCancel)) {
      // Resolves null when the dialog is dismissed.
      const pinResult = await modals.showDynamicDialog<IAuthData | null>(
        ModalAdminPin,
        {
          requiredPermission: PermissionType.OrdersCancel,
          reason: 'payment-reimburse',
        },
        'admin-pin'
      );
      if (!pinResult) return false;
      authData = pinResult;
    }

    isRefunding.value = true;
    try {
      await PaymentsService.v2SetStatus(
        payment.id,
        PaymentStatus.Reimbursed,
        authData
      );
      ui.showToast(AlertType.Success, t('posV2.paymentsRefundSuccess'));

      // Updates the store instance for pending orders and Object.assigns
      // snapshot orders opened from history, which the socket never updates.
      // The refund already happened: a failed refresh must not report failure.
      if (viewedOrder) {
        try {
          await ordersHelper.refreshOrder({ order: viewedOrder });
        } catch (err) {
          console.error('Error refreshing order after refund', err);
        }
      }
      return true;
    } catch (e) {
      ui.serverError(e, t('posV2.paymentsRefundError'));
      return false;
    } finally {
      if (!disposed) isRefunding.value = false;
    }
  }

  return { isRefunding, refundPayment };
}
